import React, { useEffect, useState, useCallback } from "react";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  InputAdornment
} from "@material-ui/core";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from "recharts";
import {
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  DateRange as DateRangeIcon
} from "@material-ui/icons";
import { format, subDays } from "date-fns";
import { es } from "date-fns/locale";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import api from "../../services/api";
import toastError from "../../errors/toastError";

registerLocale("es", es);

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc658"];

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
  },
  cardTitle: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  cardValue: {
    fontSize: 28,
    fontWeight: "bold",
    marginTop: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  cardIcon: {
    float: "right",
    color: theme.palette.action.active,
    fontSize: 40,
    opacity: 0.3,
  },
  chartContainer: {
    height: 350,
    marginTop: theme.spacing(2),
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  datePickerWrapper: {
    "& .react-datepicker-wrapper": {
      width: "auto"
    }
  }
}));

const ProfessionalTitlesStats = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ total: 0, topSpecialty: "N/A" });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");

      const { data: apiData } = await api.get("/professionalTitles-stats", {
        params: {
          start: startStr,
          end: endStr
        }
      });

      const formattedData = apiData.map((item) => ({
        name: item.title,
        value: Number(item.totalAppointments),
      })).sort((a, b) => b.value - a.value);

      const totalAppointments = formattedData.reduce((acc, curr) => acc + curr.value, 0);
      const topSpecialty = formattedData.length > 0 ? formattedData[0].name : "N/A";

      const finalData = formattedData.map((item, index) => ({
        ...item,
        percentage: totalAppointments > 0 ? ((item.value / totalAppointments) * 100).toFixed(1) : 0,
        color: COLORS[index % COLORS.length]
      }));

      setData(finalData);
      setSummary({ total: totalAppointments, topSpecialty });

    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className={classes.container}>
      <Typography variant="h6" gutterBottom style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <TrendingUpIcon color="primary" /> Estadísticas de Citas
      </Typography>

      <Paper elevation={0} variant="outlined" className={classes.filterContainer}>
        
        <Box className={classes.datePickerWrapper}>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            locale="es"
            dateFormat="dd/MM/yyyy"
            customInput={
              <TextField 
                label="Desde" 
                variant="outlined" 
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            }
          />
        </Box>

        <Box className={classes.datePickerWrapper}>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            locale="es"
            dateFormat="dd/MM/yyyy"
            customInput={
              <TextField 
                label="Hasta" 
                variant="outlined" 
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            }
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={fetchStats}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilterIcon />}
          disabled={loading}
        >
          Filtrar
        </Button>
      </Paper>

      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6}>
          <Card elevation={2}>
            <CardContent>
              <EventIcon className={classes.cardIcon} />
              <Typography className={classes.cardTitle}>Total de Citas</Typography>
              <Typography className={classes.cardValue}>
                {loading ? "..." : summary.total}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                En el periodo seleccionado
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card elevation={2}>
            <CardContent>
              <TrendingUpIcon className={classes.cardIcon} />
              <Typography className={classes.cardTitle}>Especialidad Top</Typography>
              <Typography className={classes.cardValue} style={{ fontSize: 20, marginTop: 14 }}>
                {loading ? "..." : summary.topSpecialty}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Con mayor demanda
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {data.length === 0 && !loading ? (
        <Typography className={classes.noData}>
          No hay datos para mostrar en este rango de fechas.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper elevation={2} style={{ padding: 16, height: "100%" }}>
              <Typography variant="subtitle1" gutterBottom align="center" style={{ fontWeight: 600 }}>
                Volumen de Citas por Especialidad
              </Typography>
              <div className={classes.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                    <RechartsTooltip cursor={{ fill: "transparent" }} />
                    <Legend />
                    <Bar dataKey="value" name="Citas" fill="#8884d8" radius={[0, 4, 4, 0]}>
                      {data.map((entry, index) => (
                        <Cell key={"cell-" + index} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper elevation={2} style={{ padding: 16, height: "100%" }}>
              <Typography variant="subtitle1" gutterBottom align="center" style={{ fontWeight: 600 }}>
                Distribución Porcentual
              </Typography>
              <div className={classes.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                        if (percent < 0.05) return null;
                        return (percent * 100).toFixed(0) + "%";
                      }}
                    >
                      {data.map((entry, index) => (
                        <Cell key={"cell-" + index} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name, props) => [
                        value + " citas (" + props.payload.percentage + "%)", 
                        name
                      ]} 
                    />
                    <Legend 
                      layout="horizontal" 
                      verticalAlign="bottom" 
                      align="center"
                      wrapperStyle={{ fontSize: "12px" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default ProfessionalTitlesStats;